import { useCallback, useEffect, useMemo, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { GET_USER_COMPANY_SUBCONTRACTORS } from "graphql/query/subcontractor-gql";
import {
    expandUserCompanyFilter,
    findCompanyFilter,
    getCompanyFilterText,
} from "../helpers/user-company-filter-helper";


const PAGE_SIZE = 100;
const incompleteLookup = () => new Error("Unable to load all matching companies.");

const readLookupPage = (result, requestedPage) => {
    const model = result?.data?.subcontractors;
    const info = model?.paginatorInfo;
    if (result?.errors?.length || !Array.isArray(model?.data) ||
        info?.currentPage !== requestedPage || !Number.isInteger(info?.lastPage) || info.lastPage < 1 ||
        (model.data.length === 0 && requestedPage < info.lastPage))
    {
        throw incompleteLookup();
    }

    if (model.data.some(item => item?.id == null || typeof item.title !== "string"))
    {
        throw incompleteLookup();
    }

    return model;
};

export const fetchUserCompanySubcontractorIDs = async ({ client, text }) => {
    const ids = new Set();
    const prefix = text.toLowerCase();
    let lastPage;
    let page = 1;

    do
    {
        const result = await client.query({
            query: GET_USER_COMPANY_SUBCONTRACTORS,
            variables: { text, first: PAGE_SIZE, page },
            fetchPolicy: "network-only",
            errorPolicy: "none",
        });
        const model = readLookupPage(result, page);

        model.data.forEach(({ id, title }) => {
            // The API text search can also match company numbers and descriptions.
            if (title.toLowerCase().startsWith(prefix)) ids.add(String(id));
        });

        lastPage = model.paginatorInfo.lastPage;
        page += 1;
    } while (page <= lastPage);

    return Array.from(ids);
};

const useUserCompanyFilter = (where) => {
    const client = useApolloClient();
    const pattern = findCompanyFilter(where)?.value;
    const text = getCompanyFilterText(pattern);
    const [ retryVersion, setRetryVersion ] = useState(0);
    const [ result, setResult ] = useState({ key: null, ids: [], error: null });
    const key = JSON.stringify([ text, retryVersion ]);
    const retry = useCallback(() => setRetryVersion(version => version + 1), []);

    useEffect(() => {
        let current = true;

        if (text)
        {
            fetchUserCompanySubcontractorIDs({ client, text })
                .then(ids => {
                    if (current) setResult({ key, ids, error: null });
                })
                .catch(error => {
                    if (current) setResult({ key, ids: [], error });
                });
        } else
        {
            setResult({ key: null, ids: [], error: null });
        }

        return () => {
            current = false;
        };
    }, [ client, key, text ]);

    // A new or restored filter must block the Users query on its first render.
    const loading = Boolean(text) && result.key !== key;
    const error = text && result.key === key ? result.error : null;
    const resolvedWhere = useMemo(() => (
        text && result.key === key && !result.error ?
            expandUserCompanyFilter(where, pattern, result.ids) : where
    ), [ key, pattern, result, text, where ]);

    return { where: resolvedWhere, loading, error, retry };
};

export default useUserCompanyFilter;
